import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { getRealLength, getRealSurface } from '@modules/aruco/utils/aruco-ratio.utils';
import { type WoundsizeOutput } from '@modules/forms/form-modules/woundtrack/models/woundsize-output';
import { WoundsizeMeasureSource } from '@modules/shared/workers/woundsize-measure-source.enum';
import { WoundsizeResult, WoundtrackClassProportions, WoundtypeResult } from '@pixacare/domain';
import {
  getWoundsizeResultItems,
  getWoundtypeResultItems,
  ModuleResultDisplayLabel,
  ReportElement,
} from '@pixacare/forms';
import { Polygon } from '@pixacare/geometry';
import { toPercentage } from '@pixacare/shared';

const RESULT_ITEM_LABELS: Record<ModuleResultDisplayLabel, string> = {
  [ModuleResultDisplayLabel.LENGTH]: marker('Longueur'),
  [ModuleResultDisplayLabel.WIDTH]: marker('Largeur'),
  [ModuleResultDisplayLabel.SURFACE]: marker('Surface'),
  [ModuleResultDisplayLabel.GRANULATION]: marker('Bourgeonnement'),
  [ModuleResultDisplayLabel.FIBRIN]: marker('Fibrine'),
  [ModuleResultDisplayLabel.NECROSIS]: marker('Nécrose'),
};

@Injectable({
  providedIn: 'root',
})
export class WoundtrackOutputService {
  private readonly translocoService = inject(TranslocoService);

  getWoundsizeResult(
    { polygon, length, width }: WoundsizeOutput['shapes'],
    stickerSurface: number,
    source: WoundsizeMeasureSource,
    previousResult: WoundsizeResult | null,
  ): WoundsizeResult {
    const measure = {
      length: this.formatWoundtrackResult(getRealLength(length.length, stickerSurface)),
      width: this.formatWoundtrackResult(getRealLength(width.length, stickerSurface)),
      size: this.formatWoundtrackResult(getRealSurface(polygon.area, stickerSurface)),
      vertices: this.polygonsToVertices([polygon]),
    };

    switch (source) {
      case WoundsizeMeasureSource.AI_PREDICTION:
        return {
          ...measure,
          predictedLength: measure.length,
          predictedWidth: measure.width,
          predictedSize: measure.size,
          predictedVertices: measure.vertices,
          manualLength: null,
          manualWidth: null,
          manualSize: null,
          manualVertices: null,
          isManuallyMeasured: false,
        };
      case WoundsizeMeasureSource.USER_EDIT:
        return {
          ...measure,
          predictedLength: previousResult?.predictedLength ?? null,
          predictedWidth: previousResult?.predictedWidth ?? null,
          predictedSize: previousResult?.predictedSize ?? null,
          predictedVertices: previousResult?.predictedVertices ?? null,
          manualLength: measure.length,
          manualWidth: measure.width,
          manualSize: measure.size,
          manualVertices: measure.vertices,
          isManuallyMeasured: true,
        };
    }
  }

  clearWoundsizeMeasure(result: WoundsizeResult | null): WoundsizeResult | null {
    if (!result) {
      return null;
    }

    return {
      length: null,
      width: null,
      size: null,
      vertices: null,
      predictedLength: result.predictedLength,
      predictedWidth: result.predictedWidth,
      predictedSize: result.predictedSize,
      predictedVertices: result.predictedVertices,
      manualLength: null,
      manualWidth: null,
      manualSize: null,
      manualVertices: null,
      isManuallyMeasured: result.isManuallyMeasured,
    };
  }

  getWoundsizeResultItems(result: WoundsizeResult): ReportElement[] {
    return this.translateLabels(getWoundsizeResultItems(result));
  }

  getWoundtypeResults(classProportions: WoundtrackClassProportions): WoundtypeResult {
    const total = classProportions.total - classProportions.other;
    return total
      ? {
          granulation: this.formatWoundtrackResult(toPercentage(classProportions.granulation, total)),
          fibrin: this.formatWoundtrackResult(toPercentage(classProportions.fibrin, total)),
          necrosis: this.formatWoundtrackResult(toPercentage(classProportions.necrosis, total)),
        }
      : null;
  }

  getWoundtypeResultItems(result: WoundtypeResult): ReportElement[] {
    return this.translateLabels(getWoundtypeResultItems(result));
  }

  // The items come labelled with a `ModuleResultDisplayLabel`, which is a key, not displayable text.
  private translateLabels(items: ReportElement[]): ReportElement[] {
    return items.map((item) => ({
      ...item,
      label: this.translocoService.translate(RESULT_ITEM_LABELS[item.label as ModuleResultDisplayLabel]),
    }));
  }

  private formatWoundtrackResult(value: number): number {
    return Number(value.toFixed(2));
  }

  private polygonsToVertices(polygons: Polygon[]): [number, number][][] {
    return polygons.map((polygon) =>
      polygon.points.map(
        ({ x, y }) => [this.formatWoundtrackResult(x), this.formatWoundtrackResult(y)] as [number, number],
      ),
    );
  }
}
